// POC files (/app/v1/files, ADR 0008): documents agents read; no type a browser would run.
import { z } from "zod";

const OOXML = "application/vnd.openxmlformats-officedocument";

export const FileContentType = z.enum([
  "application/pdf",
  "text/plain",
  "text/csv",
  "text/markdown",
  "application/json",
  "application/yaml",
  "image/png",
  "image/jpeg",
  "image/webp",
  `${OOXML}.wordprocessingml.document`,
  `${OOXML}.spreadsheetml.sheet`,
  `${OOXML}.presentationml.presentation`,
]);
export type FileContentType = z.infer<typeof FileContentType>;

/** Type by lower-case extension. */
export const FILE_TYPES: Readonly<Record<string, FileContentType>> = {
  pdf: "application/pdf",
  txt: "text/plain",
  csv: "text/csv",
  md: "text/markdown",
  json: "application/json",
  yaml: "application/yaml",
  yml: "application/yaml",
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  webp: "image/webp",
  docx: `${OOXML}.wordprocessingml.document`,
  xlsx: `${OOXML}.spreadsheetml.sheet`,
  pptx: `${OOXML}.presentationml.presentation`,
};

/** The browser's type when allowed, else the one of the extension (browsers leave .md or .yaml empty). */
export function contentTypeOf(name: string, reported = ""): FileContentType | null {
  const given = FileContentType.safeParse(reported.split(";")[0]?.trim().toLowerCase());
  if (given.success) return given.data;
  const dot = name.lastIndexOf(".");
  if (dot < 0) return null;
  return FILE_TYPES[name.slice(dot + 1).toLowerCase()] ?? null;
}

/** The largest single PUT of the POC (ADR 0008). */
export const MAX_FILE_BYTES = 50 * 1024 * 1024;

export const FileId = z.uuid();

/** Folder segments as the key holds them; at most five, each 1 to 60 bytes after sanitizing. */
export const FolderPath = z.array(z.string().min(1).max(60)).max(5);
export type FolderPath = z.infer<typeof FolderPath>;

/** The deepest folder (ADR 0013). */
export const MAX_FOLDER_DEPTH = 5;

export const CreateUpload = z.object({
  name: z.string().trim().min(1).max(255),
  contentType: FileContentType,
  size: z.number().int().min(0),
  folder: FolderPath.default([]),
});
export type CreateUpload = z.input<typeof CreateUpload>;

/** A presigned request: the browser sends exactly these headers. */
export const PresignedRequest = z.object({
  method: z.enum(["PUT", "GET"]),
  url: z.url(),
  headers: z.record(z.string(), z.string()),
});
export type PresignedRequest = z.infer<typeof PresignedRequest>;

export const UploadTicket = z.object({
  id: FileId,
  name: z.string(),
  upload: PresignedRequest,
  expiresAt: z.iso.datetime(),
});
export type UploadTicket = z.infer<typeof UploadTicket>;

export const StoredFile = z.object({
  id: FileId,
  name: z.string(),
  folder: FolderPath,
  size: z.number().int(),
  contentType: FileContentType.or(z.literal("application/octet-stream")),
  uploadedAt: z.iso.datetime(),
});
export type StoredFile = z.infer<typeof StoredFile>;

/** The organization's files and folders; `maxBytes` lets the Studio check sizes before asking. */
export const StoredFiles = z.object({
  files: z.array(StoredFile),
  folders: z.array(FolderPath),
  maxBytes: z.number().int(),
  /** The caller may change any file (admin or super admin). */
  manageAll: z.boolean(),
});
export type StoredFiles = z.infer<typeof StoredFiles>;

export const DownloadLink = z.object({ url: z.url(), expiresAt: z.iso.datetime() });
export type DownloadLink = z.infer<typeof DownloadLink>;

/** A rename, a move, or both; the id stays. */
export const FileChange = z
  .object({ name: z.string().trim().min(1).max(255).optional(), folder: FolderPath.optional() })
  .refine((c) => c.name !== undefined || c.folder !== undefined, "nothing to change");
export type FileChange = z.infer<typeof FileChange>;

/** New bytes for a file under the same key; the type must match its name. */
export const ReplaceUpload = z.object({
  contentType: FileContentType,
  size: z.number().int().min(0),
});
export type ReplaceUpload = z.infer<typeof ReplaceUpload>;

/** A presigned GET with an inline disposition, for the preview. */
export const ViewLink = DownloadLink;
export type ViewLink = DownloadLink;

export const CreateFolder = z.object({ folder: FolderPath.min(1) });
export type CreateFolder = z.infer<typeof CreateFolder>;

/** Types the Studio previews inline: the browser renders them in a frame or an image. */
export const VIEWABLE: ReadonlySet<string> = new Set<FileContentType>([
  "application/pdf",
  "image/png",
  "image/jpeg",
  "image/webp",
  "text/plain",
  "text/csv",
  "text/markdown",
  "application/json",
  "application/yaml",
]);

/** A folder path as one string, the way keys and URLs carry it. */
export const folderKey = (folder: readonly string[]) => folder.join("/");

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** UTF-8 length by code point; the package runs in browsers and Node without either's types. */
function utf8Bytes(text: string): number {
  let bytes = 0;
  for (const char of text) {
    const point = char.codePointAt(0) ?? 0;
    bytes += point < 0x80 ? 1 : point < 0x800 ? 2 : point < 0x10000 ? 3 : 4;
  }
  return bytes;
}

/** One key segment cleaned: NFC, control and reserved characters to `_`, spaces collapsed, dots trimmed. */
export function cleanSegment(text: string): string {
  const cleaned = text
    .normalize("NFC")
    // biome-ignore lint/suspicious/noControlCharactersInRegex: control characters are what it removes.
    .replace(/[\u0000-\u001f\u007f:*?"<>|]/g, "_")
    .replace(/\s+/g, " ");
  return trimSegment(cleaned);
}

/** Leading and trailing dots and spaces removed, so no segment reads as `.`, `..` or `.keep`. */
export const trimSegment = (text: string) => text.replace(/^[. ]+|[. ]+$/g, "");

/** A folder segment as the key holds it, or null when the API would refuse it (ADR 0013). */
export function cleanFolderSegment(segment: string): string | null {
  if (/[/\\]/.test(segment)) return null;
  const cleaned = cleanSegment(segment);
  if (!cleaned || utf8Bytes(cleaned) > 60 || UUID.test(cleaned)) return null;
  return cleaned;
}
